/**
 * Blog Index — Journal & Styling Guides.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Reveal } from "@/components/common/reveal";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { sampleBlogPosts } from "@/features/blog/services/blog-service";
import { siteConfig } from "@/config/site";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: `Journal & Styling Guides — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Explore botanical styling guides, craft notes, and interior inspiration from Aster Decorz.",
      },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/blog` }],
  }),
  component: BlogIndex,
});

const CATEGORIES = ["All", "Styling Guides", "Craft & Materials", "Commercial Interiors"];

function BlogIndex() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredPosts =
    activeCategory === "All"
      ? sampleBlogPosts
      : sampleBlogPosts.filter((p) => p.category === activeCategory);

  const featuredPost = sampleBlogPosts[0];

  return (
    <div className="section-container pb-28 pt-24 md:pt-28">
      <Reveal>
        <p className="eyebrow">The Journal</p>
        <h1 className="mt-6 max-w-3xl">Stories on light, proportion, and quiet interiors</h1>
      </Reveal>

      {/* Featured post banner */}
      {featuredPost && (
        <Reveal delay={100} className="mt-14">
          <Link
            to="/blog/$slug"
            params={{ slug: featuredPost.slug }}
            className="group grid gap-8 lg:grid-cols-12 lg:items-center border bg-card overflow-hidden hover:border-primary/50 transition-colors"
          >
            <div className="lg:col-span-7 overflow-hidden">
              <img
                src={featuredPost.coverImage}
                alt={featuredPost.title}
                className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 lg:col-span-5 lg:p-12">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="eyebrow text-primary">{featuredPost.category}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {featuredPost.readingTime}
                </span>
              </div>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl group-hover:text-primary transition-colors">
                {featuredPost.title}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {featuredPost.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-medium text-foreground">
                <span>Read Story</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        </Reveal>
      )}

      {/* Category filters */}
      <div className="mt-20 flex flex-wrap gap-x-8 gap-y-3 border-b pb-5">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`text-xs tracking-[0.18em] uppercase transition-colors ${
              activeCategory === cat
                ? "text-foreground font-medium border-b-2 border-primary pb-5 -mb-5.5"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Post grid */}
      <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {filteredPosts.map((post, i) => (
          <Reveal key={post.id} delay={i * 80}>
            <article className="group flex flex-col h-full border bg-card overflow-hidden hover:border-primary/50 transition-colors">
              <Link to="/blog/$slug" params={{ slug: post.slug }} className="block overflow-hidden">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </Link>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="eyebrow">{post.category}</span>
                    <span>{post.readingTime}</span>
                  </div>
                  <Link to="/blog/$slug" params={{ slug: post.slug }}>
                    <h3 className="mt-3 font-display text-xl leading-tight group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground line-clamp-3">
                    {post.excerpt}
                  </p>
                </div>
                <div className="mt-6 border-t pt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span>By {post.authorName}</span>
                  <span>{post.publishedAt}</span>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
